/**
 * Frontend-Service: Push-Subscription-Management
 *
 * Verbindet Browser Push API mit unserem Backend:
 *   1. Service Worker registrieren (falls noch nicht)
 *   2. VAPID Public Key vom Backend holen
 *   3. Browser-subscribe(applicationServerKey) → Endpoint + Keys
 *   4. POST an Backend zum Speichern
 *
 * iOS-Spezifika:
 *   - Web-Push funktioniert NUR bei zum Home-Bildschirm hinzugefügten PWAs
 *   - Detection via window.matchMedia('(display-mode: standalone)')
 *   - Wenn nicht-standalone: subscribe() würde ohnehin fehlschlagen, daher
 *     vorher abfragen und passenden Hinweis im UI anzeigen
 */

import apiClient from '../api/apiClient';

// ============================================================================
// FEATURE-DETECTION
// ============================================================================

export interface PushCapability {
  /** Push API + Service Worker grundsätzlich verfügbar im Browser? */
  isSupported: boolean;
  /** Ist der Browser im PWA-Standalone-Modus? (iOS-Voraussetzung) */
  isStandalone: boolean;
  /** Ist es ein iOS-Gerät? Beeinflusst Onboarding-Hinweise */
  isIOS: boolean;
  /** Ist es ein Android-Gerät? Beeinflusst Spam-Filter-Hinweis (Chrome
   *  filtert die erste Push einer unbekannten Quelle als Spam-Vermutung) */
  isAndroid: boolean;
  /** Aktueller Permission-Status */
  permission: NotificationPermission | 'unsupported';
  /** Detail-Flags für Debugging */
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  isSecureContext: boolean;
  userAgent: string;
}

export function getPushCapability(): PushCapability {
  if (typeof window === 'undefined') {
    return {
      isSupported: false,
      isStandalone: false,
      isIOS: false,
      isAndroid: false,
      permission: 'unsupported',
      hasServiceWorker: false,
      hasPushManager: false,
      hasNotification: false,
      isSecureContext: false,
      userAgent: ''
    };
  }

  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  const isSupported = hasServiceWorker && hasPushManager && hasNotification;

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  const userAgent = window.navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  const permission: NotificationPermission | 'unsupported' = hasNotification
    ? Notification.permission
    : 'unsupported';

  return {
    isSupported,
    isStandalone,
    isIOS,
    isAndroid,
    permission,
    hasServiceWorker,
    hasPushManager,
    hasNotification,
    isSecureContext: window.isSecureContext === true,
    userAgent
  };
}

// ============================================================================
// SERVICE WORKER REGISTRIERUNG
// ============================================================================

let swRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null;

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }
  return swRegistrationPromise;
}

// ============================================================================
// VAPID-KEY-LOADER (mit kleinem Memo)
// ============================================================================

let cachedVapidKey: Uint8Array | null = null;

async function getVapidKey(): Promise<Uint8Array> {
  if (cachedVapidKey) return cachedVapidKey;
  const res = await apiClient.get<{ success: true; data: { publicKey: string } }>(
    '/support/push-subscriptions/vapid-public-key'
  );
  cachedVapidKey = urlBase64ToUint8Array(res.data.data.publicKey);
  return cachedVapidKey;
}

/** VAPID-Keys werden URL-safe-Base64 ausgeliefert. Browser braucht Uint8Array. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf;
}

// ============================================================================
// HAUPT-API
// ============================================================================

/**
 * Komplettes Onboarding in einem Aufruf:
 *   1. Permission anfragen (falls noch 'default')
 *   2. Service Worker registrieren
 *   3. Beim Push-Service subscriben
 *   4. Backend informieren
 *
 * Wirft beschreibenden Error wenn:
 *   - Browser-Support fehlt
 *   - User die Permission abgelehnt hat
 *   - iOS aber nicht im PWA-Mode
 */
export async function enablePushNotifications(): Promise<void> {
  const cap = getPushCapability();

  if (!cap.isSupported) {
    throw new Error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
  }

  if (cap.isIOS && !cap.isStandalone) {
    throw new Error(
      'Auf dem iPhone funktionieren Push-Benachrichtigungen nur, wenn die Website über den Home-Bildschirm geöffnet wird.'
    );
  }

  // Permission anfragen wenn noch 'default'
  if (cap.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      throw new Error('Berechtigung wurde nicht erteilt.');
    }
  } else if (cap.permission === 'denied') {
    throw new Error(
      'Du hast Benachrichtigungen vorher abgelehnt. Bitte aktiviere sie in den Browser-Einstellungen.'
    );
  }

  // SW registrieren + Subscription erstellen
  const registration = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  // Bestehende Subscription? Dann re-nutzen
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const vapidKey = await getVapidKey();
    // ArrayBuffer aus Uint8Array — applicationServerKey verlangt strict ArrayBuffer
    // (nicht SharedArrayBuffer). Cast über new Uint8Array(...).buffer ist sicher.
    const vapidBuffer = new Uint8Array(vapidKey).buffer;
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidBuffer
    });
  }

  // An Backend senden
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Subscription unvollständig.');
  }

  await apiClient.post('/support/push-subscriptions', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    platform: detectPlatform()
  });
}

/**
 * Aktive Subscription auf diesem Gerät entfernen (vom Backend + Browser).
 */
export async function disablePushNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  // Erst Backend (so wissen wir die endpoint), dann Browser
  try {
    await apiClient.delete('/support/push-subscriptions', {
      data: { endpoint: subscription.endpoint }
    });
  } catch {
    // Best-Effort — auch wenn Backend nicht erreichbar, lokal abmelden
  }
  await subscription.unsubscribe();
}

/**
 * Erzwingt eine frische Subscription auf diesem Gerät.
 *
 * Nutzungsfall:
 *   Backend hat die Subscription deaktiviert (404/410 vom Push-Service),
 *   weil der Endpoint vom Browser/FCM rotiert wurde — passiert z.B. nach
 *   Storage-Cleanup, längerer Inaktivität oder OS-Updates. Der lokale
 *   Browser hält evtl. noch eine `getSubscription()` mit demselben toten
 *   Endpoint — `enablePushNotifications()` würde diese wiederverwenden
 *   und das Backend wieder mit demselben kaputten Endpoint befüllen.
 *
 * Lösung: bestehende Sub lokal verwerfen, neu subscriben, Backend-UPSERT
 * setzt is_active wieder auf true (oder legt einen neuen Eintrag an, falls
 * der neue Endpoint sich vom alten unterscheidet).
 */
export async function forceResubscribePush(): Promise<void> {
  const cap = getPushCapability();

  if (!cap.isSupported) {
    throw new Error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
  }
  if (cap.isIOS && !cap.isStandalone) {
    throw new Error(
      'Auf dem iPhone funktionieren Push-Benachrichtigungen nur, wenn die Website über den Home-Bildschirm geöffnet wird.'
    );
  }
  if (cap.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      throw new Error('Berechtigung wurde nicht erteilt.');
    }
  } else if (cap.permission === 'denied') {
    throw new Error(
      'Du hast Benachrichtigungen vorher abgelehnt. Bitte aktiviere sie in den Browser-Einstellungen.'
    );
  }

  const registration = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  // Bestehende Subscription verwerfen — lokal und im Backend
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    try {
      await apiClient.delete('/support/push-subscriptions', {
        data: { endpoint: existing.endpoint }
      });
    } catch {
      /* Best-Effort */
    }
    try {
      await existing.unsubscribe();
    } catch {
      /* manche Browser werfen hier — egal, neue Sub kommt gleich */
    }
  }

  // Neu subscriben
  const vapidKey = await getVapidKey();
  const vapidBuffer = new Uint8Array(vapidKey).buffer;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidBuffer
  });

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Subscription unvollständig.');
  }

  await apiClient.post('/support/push-subscriptions', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    platform: detectPlatform()
  });
}

// ============================================================================
// DEVICE-STATUS — kombiniert lokale Browser-Sub mit Backend-DB-Status
// ============================================================================

export type DevicePushState =
  /** Browser unterstützt Push nicht (alter Browser, Inkognito, etc.) */
  | 'unsupported'
  /** iOS aber nicht im PWA-Modus → Push ist im normalen Safari nicht möglich */
  | 'ios-needs-pwa'
  /** User hat Notifications im Browser explizit abgelehnt */
  | 'permission-denied'
  /** Push noch nie auf diesem Gerät aktiviert (keine lokale Sub vorhanden) */
  | 'inactive'
  /** Lokale Sub UND Backend-Eintrag aktiv → alles gut */
  | 'active'
  /** Lokale Sub vorhanden, aber Backend hat sie deaktiviert (z.B. nach 410)
   *  → "Neu verbinden"-Flow nötig */
  | 'stale';

export interface DevicePushStatus {
  state: DevicePushState;
  /** Vollständiger Endpoint der lokalen Sub — wird für Test-Push an dieses Gerät benötigt */
  localEndpoint: string | null;
}

/**
 * Berechnet einen stabilen Hash über den Endpoint, identisch zur Backend-Logik
 * (sha256 → hex → erste 16 Zeichen). Erlaubt Geräte-Matching ohne den vollen
 * Endpoint übers Netz schicken zu müssen.
 */
async function hashEndpoint(endpoint: string): Promise<string> {
  const data = new TextEncoder().encode(endpoint);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

/**
 * Berechnet den effektiven Push-Status für genau dieses Gerät.
 * Kombiniert browserseitige `getSubscription()` mit den Backend-Daten
 * aus `/support/push-subscriptions/status`.
 */
export async function fetchDevicePushStatus(): Promise<DevicePushStatus> {
  const cap = getPushCapability();
  if (!cap.isSupported) return { state: 'unsupported', localEndpoint: null };
  if (cap.isIOS && !cap.isStandalone) return { state: 'ios-needs-pwa', localEndpoint: null };
  if (cap.permission === 'denied') return { state: 'permission-denied', localEndpoint: null };

  // Lokale Sub holen
  const registration = await navigator.serviceWorker.getRegistration('/');
  const localSub = registration ? await registration.pushManager.getSubscription() : null;

  if (!localSub) {
    return { state: 'inactive', localEndpoint: null };
  }

  const localEndpoint = localSub.endpoint;

  // Backend-Status holen und prüfen ob unsere lokale Sub aktiv ist
  try {
    const [localFingerprint, diag] = await Promise.all([
      hashEndpoint(localEndpoint),
      fetchPushDiagnose()
    ]);

    const match = diag.subscriptions.find((s) => s.endpointFingerprint === localFingerprint);
    if (!match) {
      // Lokal vorhanden, aber Backend kennt sie nicht → noch nicht hochgeladen
      return { state: 'inactive', localEndpoint };
    }
    return { state: match.isActive ? 'active' : 'stale', localEndpoint };
  } catch {
    // Backend-Fehler — fallback: lokale Sub allein als "wahrscheinlich aktiv" werten
    return { state: 'active', localEndpoint };
  }
}

/**
 * Sendet einen Test-Push gezielt an die Subscription dieses Geräts.
 * Wirft, wenn keine lokale Sub vorhanden oder Backend ablehnt.
 */
export async function sendTestPushThisDevice(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker nicht verfügbar.');
  }
  const registration = await navigator.serviceWorker.getRegistration('/');
  const sub = registration ? await registration.pushManager.getSubscription() : null;
  if (!sub) {
    throw new Error('Auf diesem Gerät ist Push nicht aktiv.');
  }
  await apiClient.post('/support/push-subscriptions/test-this-device', {
    endpoint: sub.endpoint
  });
}

/**
 * Prüft ob auf diesem Gerät bereits eine Push-Subscription besteht.
 * Wird von der Onboarding-UI genutzt um nicht erneut zu fragen.
 */
export async function hasActivePushSubscription(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

// ============================================================================
// DIAGNOSE-API
// ============================================================================

export interface PushDiagnose {
  vapidConfigured: boolean;
  activeSubscriptionCount: number;
  wsOnline: boolean;
  subscriptions: Array<{
    id: string;
    endpointHostHint: string | null;
    /** Stabiler Hash des Endpoints — fürs Matching mit der lokalen Sub */
    endpointFingerprint: string | null;
    platform: string;
    isActive: boolean;
    createdAt: string;
    lastUsedAt: string | null;
  }>;
}

export async function fetchPushDiagnose(): Promise<PushDiagnose> {
  const res = await apiClient.get<{ success: true; data: PushDiagnose }>(
    '/support/push-subscriptions/status'
  );
  return res.data.data;
}

export async function sendTestPush(): Promise<void> {
  await apiClient.post('/support/push-subscriptions/test', {});
}

function detectPlatform(): 'ios' | 'android' | 'desktop' | 'other' {
  if (typeof window === 'undefined') return 'other';
  const ua = window.navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Mac|Win|Linux/.test(ua)) return 'desktop';
  return 'other';
}
